import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetPaymentLinesReportData } from '../../../hooks/reports/queries';
import { exportPaymentLinesReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { getInitialReportRange, getRangeDate, getReportExclusiveEnd, getReportTimezone } from '../datePickerHelper';

const PaymentLinesReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>(() => getInitialReportRange(location.search));
  const [appliedRange, setAppliedRange] = useState(dateRangePicker);
  const updateDateRange = (value: [DateValue, DateValue]) => {
    setDateRange(value);
    if (value[0] && value[1]) {
      setAppliedRange(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }
  };
  const limitPerReq = 10;
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: limitPerReq
  });
  const timezone = getReportTimezone(location.search);
  const { fromDate, toDate } = getRangeDate(appliedRange, 'day', timezone, getReportExclusiveEnd(location.search));
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetPaymentLinesReportData(searchParamStoreId, {
    fromDate,
    toDate,
    timezone: timezone,
    pageNum: pagination.pageIndex,
    limit: limitPerReq
  });

  const tableInfo = useMemo(() => {
    return data?.pages?.reduce((list: any[], { graphInfo }: { graphInfo: any[] }) => {
      list.push(...graphInfo);
      return list;
    }, [] as any[]);
  }, [data]);

  const footerInfo = useMemo(() => {
    return {
      totalAmountPaid: data?.pages[0]?.totalAmountPaid || 0,
      totalFeeTotal: data?.pages[0]?.totalFeeTotal || 0,
      totalNetAmount: data?.pages[0]?.totalNetAmount || 0,
    };
  }, [data?.pages]);

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / limitPerReq) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:paymentLinesReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={updateDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportPaymentLinesReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: timezone
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfo={tableInfo}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
            footerInfo={footerInfo}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default PaymentLinesReport;
